import { z } from 'zod';
import { Ulid, UserIdentity } from './common.js';
import { FeedbackReport, FeedbackReportChannel } from './feedback.js';
/**
 * Tickets (`docs/tickets-plan.md`) — a game's feedback as a small, fixed issue tracker.
 *
 * Every report a player sends becomes a ticket in **New**; a person accepting it moves it to
 * **To Do**, and the playground's assistant works To Do one ticket at a time. The fields are a
 * JIRA work item's core and nothing more (§1 of the plan says what was left out and why), and
 * none of them is configurable: there is no workflow editor, no custom field and no colour.
 */
/* ------------------------------------------------------------------------------- vocabulary */
/**
 * Six statuses, fixed (§0.2). A person may move a ticket from any of them to any other; the
 * only rules are the assistant's (`TICKET_ASSISTANT_STATUSES`).
 *
 * **Blocked** (owner, 2026-10-20) is accepted work that cannot move until something outside it
 * does — a decision that is the developer's, more detail from the player, a fix somewhere else.
 * It is open, and it says why in its thread.
 */
export const TicketStatus = z.enum(['new', 'todo', 'in_progress', 'blocked', 'done', 'wont_do']);
/** In the order a list shows them. */
export const TICKET_STATUSES = TicketStatus.options;
/** What "open" means as a filter: everything nobody has closed. */
export const TICKET_OPEN_STATUSES = ['new', 'todo', 'in_progress', 'blocked'];
/**
 * Open and accepted: what a person took on and nobody has finished. The other half of an open
 * count beside New, which is what nobody has looked at yet (the games list, the game's column).
 */
export const TICKET_ACCEPTED_STATUSES = ['todo', 'in_progress', 'blocked'];
/** Entering one of these sets `closedAt`; leaving it clears it. JIRA's resolution, folded in. */
export const TICKET_CLOSED_STATUSES = ['done', 'wont_do'];
/**
 * The statuses the playground's assistant may set, and only from a ticket that is in one of them
 * already (§0.8) — the accepted ones and Done. New is out because accepting a report is a person's
 * decision — and that acceptance is also what stands between a stranger's sentence and an agent
 * that acts on it (§6). Won't Do is out because rejecting work is a person's decision too. Blocked
 * is in: it is how the assistant says it has stopped on a question, and it comments the question.
 */
export const TICKET_ASSISTANT_STATUSES = [...TICKET_ACCEPTED_STATUSES, 'done'];
export const TicketType = z.enum(['bug', 'feature', 'task']);
export const TicketPriority = z.enum(['high', 'medium', 'low']);
/** Where a ticket came from. A report's ticket was written by nobody on the team. */
export const TicketOrigin = z.enum(['report', 'dashboard', 'playground']);
export const MAX_TICKET_TITLE_CHARS = 200;
export const MAX_TICKET_TEXT_CHARS = 20_000;
export const MAX_TICKET_TAGS = 10;
export const MAX_TICKET_TAG_CHARS = 32;
/** How much of a player's first line becomes a report's title (§3.2). */
export const TICKET_TITLE_FROM_REPORT_CHARS = 80;
/** One request's worth of list. The assistant asks for fewer: Codex cuts tool output short. */
export const MAX_TICKET_PAGE = 200;
/**
 * A tag, normalised: lowercase, single spaces, 1–32 of letters, digits, spaces, `-` and `_`,
 * starting with a letter or digit.
 *
 * Free text the team types rather than a list somebody maintains (§2) — so the normalising is
 * what makes `UI`, `ui ` and `ui` one tag rather than three.
 */
export const TicketTag = z
    .string()
    .transform((value) => value.trim().toLowerCase().replace(/\s+/g, ' '))
    .refine((value) => /^[a-z0-9][a-z0-9 _-]*$/.test(value) && value.length <= MAX_TICKET_TAG_CHARS, {
    message: `A tag is 1–${MAX_TICKET_TAG_CHARS} letters, digits, spaces, "-" or "_", starting with a letter or digit.`,
});
const TicketTitle = z.string().trim().min(1).max(MAX_TICKET_TITLE_CHARS);
const TicketText = z.string().trim().max(MAX_TICKET_TEXT_CHARS);
const TicketTags = z
    .array(TicketTag)
    .max(MAX_TICKET_TAGS)
    .transform((tags) => [...new Set(tags)]);
/**
 * A report's title: the player's first non-empty line, cut at a word boundary.
 *
 * It stays theirs until a person rewrites it — the ticket's `titleFromReport` says which — and
 * the assistant is shown it inside the fence until then.
 */
export function ticketTitleFromReport(message) {
    const line = message
        .split(/\r?\n/)
        .map((part) => part.trim())
        .find((part) => part.length > 0) ?? '';
    if (line.length === 0)
        return 'Untitled report';
    if (line.length <= TICKET_TITLE_FROM_REPORT_CHARS)
        return line;
    const room = TICKET_TITLE_FROM_REPORT_CHARS - 1;
    const cut = line.slice(0, room);
    const space = cut.lastIndexOf(' ');
    // A boundary in the last third is worth keeping; earlier than that, a long word is cut instead.
    return `${(space > room * 0.66 ? cut.slice(0, space) : cut).trimEnd()}…`;
}
/* ---------------------------------------------------------------------------------- reads */
/** One line of a list. */
export const TicketSummary = z.object({
    id: Ulid,
    appId: Ulid,
    number: z.number().int(),
    type: TicketType,
    title: z.string(),
    /** True while the title is still the player's first line — nobody on the team has rewritten it. */
    titleFromReport: z.boolean(),
    status: TicketStatus,
    priority: TicketPriority,
    assignee: UserIdentity.nullable(),
    tags: z.array(z.string()),
    createdVia: TicketOrigin,
    /** The channel its report came from; null for a ticket written by hand. */
    channel: FeedbackReportChannel.nullable(),
    /** True when it came from a report; the report itself is on `Ticket`. */
    hasReport: z.boolean(),
    createdAt: z.number().int(),
    updatedAt: z.number().int(),
    closedAt: z.number().int().nullable(),
});
export const TicketField = z.enum(['status', 'priority', 'assignee', 'type']);
/**
 * One entry of the thread: a comment, or a change of one of `TicketField`.
 *
 * `via` is `playground` when the assistant wrote it; `author` is then the person whose playground
 * it is — the assistant is somebody's assistant (`actingCaller`). For an assignee change, `from`
 * and `to` are the people's names as this reader may see them, not their ids.
 */
export const TicketEntry = z.object({
    id: Ulid,
    kind: z.enum(['comment', 'change']),
    author: UserIdentity.nullable(),
    via: z.enum(['dashboard', 'playground']),
    body: z.string().nullable(),
    field: TicketField.nullable(),
    from: z.string().nullable(),
    to: z.string().nullable(),
    createdAt: z.number().int(),
});
/** One ticket, whole. */
export const Ticket = TicketSummary.extend({
    description: z.string(),
    createdBy: UserIdentity.nullable(),
    /** What the player said, with the build, device and screenshot state — null for a hand-written one. */
    report: FeedbackReport.nullable(),
    entries: z.array(TicketEntry),
});
export const TicketCounts = z.object({
    new: z.number().int(),
    todo: z.number().int(),
    in_progress: z.number().int(),
    blocked: z.number().int(),
    done: z.number().int(),
    wont_do: z.number().int(),
});
export const TicketPage = z.object({
    items: z.array(TicketSummary),
    nextCursor: z.string().nullable(),
    /**
     * First page only: how many tickets are in each status under every filter *but* the status one
     * — the numbers on the status chips.
     */
    counts: TicketCounts.optional(),
    /** First page only: every tag in use in this game, for the filter and the tag field. */
    tags: z.array(z.string()).optional(),
});
/**
 * How a list is ordered. `priority` is the backlog: High first, and oldest first within a
 * priority, which is the order the assistant works in. `newest` is the inbox.
 */
export const TicketSort = z.enum(['priority', 'newest', 'oldest']);
/* --------------------------------------------------------------------------------- writes */
/**
 * `POST /v1/apps/:appId/tickets`. A hand-written ticket starts in To Do: whoever wrote it has
 * accepted it. The assistant may not set `priority` or `assigneeId` (§0.8).
 */
export const CreateTicketRequest = z.object({
    type: TicketType.default('task'),
    title: TicketTitle,
    description: TicketText.default(''),
    priority: TicketPriority.optional(),
    assigneeId: Ulid.nullable().optional(),
    tags: TicketTags.default([]),
});
/**
 * `PATCH /v1/apps/:appId/tickets/:number`. Every field optional; at least one present.
 *
 * Tags either replace the list (`tags`, what the dashboard's field sends) or move by one
 * (`addTags` / `removeTags`, what the assistant sends) — not both in one request. The assistant
 * may send `status`, within `TICKET_ASSISTANT_STATUSES`, and the tags, and nothing else.
 */
export const UpdateTicketRequest = z
    .object({
    title: TicketTitle.optional(),
    description: TicketText.optional(),
    type: TicketType.optional(),
    status: TicketStatus.optional(),
    priority: TicketPriority.optional(),
    assigneeId: Ulid.nullable().optional(),
    tags: TicketTags.optional(),
    addTags: TicketTags.optional(),
    removeTags: TicketTags.optional(),
})
    .refine((value) => Object.values(value).some((field) => field !== undefined), {
    message: 'Nothing to change.',
})
    .refine((value) => value.tags === undefined || (value.addTags === undefined && value.removeTags === undefined), {
    message: 'Send either "tags" or "addTags"/"removeTags", not both.',
    path: ['tags'],
});
export const CreateTicketCommentRequest = z.object({
    body: z.string().trim().min(1).max(MAX_TICKET_TEXT_CHARS),
});
/** The most tickets one bulk change names. The dashboard sends a larger selection in parts. */
export const MAX_TICKET_BULK = 200;
/**
 * `POST /v1/apps/:appId/tickets/bulk` — one status for several tickets at once, from the list's
 * selection. A person's only: the assistant moves one ticket at a time, and its status rules
 * (`TICKET_ASSISTANT_STATUSES`) are about the ticket it is moving.
 */
export const BulkUpdateTicketsRequest = z.object({
    numbers: z
        .array(z.number().int().positive())
        .min(1)
        .max(MAX_TICKET_BULK)
        .transform((numbers) => [...new Set(numbers)]),
    status: TicketStatus,
});
export const BulkUpdateTicketsResponse = z.object({
    /**
     * The tickets whose status changed. One that was in that status already, or that is no longer
     * in the game, is not among them, and nothing was written for it.
     */
    changed: z.array(z.number().int()),
    /** Every changed ticket's new `updatedAt`, and its `closedAt` when the status closes it. */
    at: z.number().int(),
});
//# sourceMappingURL=tickets.js.map